import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  Action,
  Selector,
  State,
  StateContext,
  Store,
  createSelector,
} from '@ngxs/store';
import {
  ProductivitiesResponse,
  Productivity,
  ProductivityKPIGatewayService,
} from '@wilson/api/gateway';
import { ManyEntity } from '@wilson/base';
import { Shift, WithPreparedAttributes } from '@wilson/interfaces';
import { ShiftsService } from '@wilson/shifts';
import { endOfDay, startOfDay } from 'date-fns';
import { cloneDeep } from 'lodash';
import { NzMessageService } from 'ng-zorro-antd/message';
import {
  Observable,
  Subject,
  catchError,
  filter,
  finalize,
  map,
  of,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { TimelineDataUtilityStateModel } from '../interfaces';
import { ShiftTimelineDataState } from '../shift-timeline-data/shift-timeline-data.state';
import { defaultTimelineDataUtilityState } from '../shift-timeline-default-data';
import { determineUnreleasedAndDeclineShifts } from './determine-unreleased-and-decline-shifts';
import {
  FetchProductivityForAllFilteredUsers,
  RemoveUnreleasedShifts,
  UpdateAssignedShiftsKPIs,
  UpdateProductivityOfUsers,
} from './timeline-data-utility.actions';

@State({
  name: 'timelineDataStateUtility',
  defaults: defaultTimelineDataUtilityState,
})
@Injectable()
export class TimelineDataUtilityState {
  private store = inject(Store);
  private shiftsService = inject(ShiftsService);
  private productivityKPIGatewayService = inject(ProductivityKPIGatewayService);
  private readonly nzMessageService = inject(NzMessageService);
  private readonly translateService = inject(TranslateService);

  private unsubFetchUnPublishedShiftsRequestSubject = new Subject();

  @Selector()
  static unreleasedShifts(
    state: TimelineDataUtilityStateModel,
  ): (Shift & WithPreparedAttributes)[] {
    return state.unreleasedShifts;
  }

  @Selector()
  static declinedShifts(
    state: TimelineDataUtilityStateModel,
  ): (Shift & WithPreparedAttributes)[] {
    return state.declinedShifts;
  }

  @Selector()
  static isLoadingUnreleasedShifts(
    state: TimelineDataUtilityStateModel,
  ): boolean {
    return state.isLoadingUnreleasedShifts;
  }

  static filteredUnreleasedShifts(
    userIds: string[],
  ): (
    unreleasedShifts: (Shift & WithPreparedAttributes)[],
  ) => (Shift & WithPreparedAttributes)[] {
    return createSelector(
      [TimelineDataUtilityState.unreleasedShifts],
      (unreleasedShifts: (Shift & WithPreparedAttributes)[]) => {
        if (userIds) {
          return unreleasedShifts.filter((unreleasedShift) =>
            userIds.includes(unreleasedShift.userId as string),
          );
        } else {
          return [];
        }
      },
    );
  }

  static filteredDeclinedShifts(
    userIds: string[],
  ): (
    declinedShifts: (Shift & WithPreparedAttributes)[],
  ) => (Shift & WithPreparedAttributes)[] {
    return createSelector(
      [TimelineDataUtilityState.declinedShifts],
      (declinedShifts: (Shift & WithPreparedAttributes)[]) => {
        if (userIds) {
          return declinedShifts.filter((declinedShifts) =>
            userIds.includes(declinedShifts.userId as string),
          );
        } else {
          return [];
        }
      },
    );
  }

  @Action(RemoveUnreleasedShifts)
  updateUnreleasedShifts(
    ctx: StateContext<TimelineDataUtilityStateModel>,
    action: RemoveUnreleasedShifts,
  ): void {
    const state = ctx.getState();
    const unreleasedShifts = cloneDeep(state.unreleasedShifts);
    const unreleasedShiftIndex = unreleasedShifts.findIndex(
      (shift) => shift.id === action.releasedShiftId,
    );

    if (unreleasedShiftIndex) {
      unreleasedShifts.splice(unreleasedShiftIndex, 1);
      ctx.patchState({
        unreleasedShifts,
      });
    }
  }

  @Action(UpdateAssignedShiftsKPIs)
  updateAssignedShiftsKPIs(
    ctx: StateContext<TimelineDataUtilityStateModel>,
    action: UpdateAssignedShiftsKPIs,
  ): Observable<ManyEntity<Shift & WithPreparedAttributes>> {
    this.unsubFetchUnPublishedShiftsRequestSubject.next(null);

    const { visibleDateRange, isCellView } = action;

    ctx.patchState({
      isLoadingUnreleasedShifts: true,
    });

    const hasSuccessfullyFetchedUsers$ = this.store.select(
      ShiftTimelineDataState.hasSuccessfullyFetchedUsers,
    );

    return hasSuccessfullyFetchedUsers$.pipe(
      filter((success) => success),
      switchMap(() =>
        this.createFetchAssignedShiftsRequest(
          visibleDateRange.start,
          visibleDateRange.end,
        ).pipe(
          catchError(() =>
            of({
              data: [],
              count: 0,
            }),
          ),
          tap(
            ({
              data: assignedShifts,
            }: ManyEntity<Shift & WithPreparedAttributes & { id: string }>) => {
              const result = determineUnreleasedAndDeclineShifts(
                assignedShifts,
                visibleDateRange,
                isCellView,
              );

              ctx.patchState({
                unreleasedShifts: result.unreleasedShifts,
                declinedShifts: result.declinedShifts,
                isLoadingUnreleasedShifts: false,
              });
            },
          ),
        ),
      ),
    );
  }

  private createFetchAssignedShiftsRequest(
    startDate: number,
    endDate: number,
  ): Observable<ManyEntity<Shift & WithPreparedAttributes & { id: string }>> {
    const startDateStartOfDay = startOfDay(startDate);
    const endDateEndOfDay = endOfDay(endDate);

    return this.shiftsService
      .getShifts<Shift & WithPreparedAttributes & { id: string }>({
        where: {
          startDate: [
            startDateStartOfDay.toISOString(),
            endDateEndOfDay.toISOString(),
          ],
          userId: 'not-null',
        },
        limit: 0,
        offset: 0,
        order: {},
      })
      .pipe(takeUntil(this.unsubFetchUnPublishedShiftsRequestSubject));
  }

  @Selector()
  static isLoadingUsersProductivity(
    state: TimelineDataUtilityStateModel,
  ): boolean {
    return state.isLoadingUsersProductivity;
  }
  @Selector()
  static usersProductivities(
    state: TimelineDataUtilityStateModel,
  ): Record<string, Productivity> {
    return state.productivitiesResponse.usersProductivities;
  }

  @Selector()
  static hasFetchedUsersProductivitySuccessfully(
    state: TimelineDataUtilityStateModel,
  ): boolean {
    return state.hasFetchedUsersProductivitySuccessfully;
  }

  @Selector()
  static loadingProductivityUserIds(
    state: TimelineDataUtilityStateModel,
  ): string[] {
    return state.loadingProductivityUserIds;
  }

  @Selector()
  static usersOverallProductivity(
    state: TimelineDataUtilityStateModel,
  ): Productivity {
    return state.productivitiesResponse.overallProductivity;
  }

  static userProductivityPercentageStream(
    userId: string,
  ): (usersProductivity: Record<string, Productivity>) => number {
    return createSelector(
      [TimelineDataUtilityState.usersProductivities],
      (usersProductivity: Record<string, Productivity>) => {
        return usersProductivity[userId]?.productivityRatio;
      },
    );
  }

  @Action(FetchProductivityForAllFilteredUsers)
  FetchProductivityForAllFilteredUsers(
    ctx: StateContext<TimelineDataUtilityStateModel>,
    action: FetchProductivityForAllFilteredUsers,
  ): never[] | Observable<void> {
    const userIds = action.userIds;
    const visibleDateRange = this.store.selectSnapshot(
      ShiftTimelineDataState.visibleDateRange,
    );
    if (visibleDateRange.start && visibleDateRange.end) {
      ctx.patchState({
        isLoadingUsersProductivity: action.showLoading,
      });
      return this.productivityKPIGatewayService
        .getProductivities(userIds, {
          startDatetime: startOfDay(
            new Date(visibleDateRange.start),
          ).toISOString(),
          endDatetime: endOfDay(new Date(visibleDateRange.end)).toISOString(),
        })
        .pipe(
          map((productivitiesResponse: ProductivitiesResponse) => {
            ctx.patchState({
              productivitiesResponse,
              hasFetchedUsersProductivitySuccessfully: true,
            });
          }),
          catchError(() => {
            this.nzMessageService.error(
              this.translateService.instant('general.error'),
            );
            return [];
          }),
          finalize(() => {
            ctx.patchState({
              isLoadingUsersProductivity: false,
            });
          }),
        );
    } else {
      console.warn(
        'Warning: Cannot fetch productivity for invalid time frame',
        visibleDateRange,
      );
      ctx.patchState({
        productivitiesResponse:
          defaultTimelineDataUtilityState.productivitiesResponse,
        isLoadingUsersProductivity: false,
        hasFetchedUsersProductivitySuccessfully: true,
      });
      return [];
    }
  }

  @Action(UpdateProductivityOfUsers)
  updateProductivityOfUsers(
    ctx: StateContext<TimelineDataUtilityStateModel>,
    action: UpdateProductivityOfUsers,
  ): never[] | Observable<void> {
    const userIds = action.userIds;
    const visibleDateRange = this.store.selectSnapshot(
      ShiftTimelineDataState.visibleDateRange,
    );

    if (visibleDateRange.end && visibleDateRange.start) {
      ctx.patchState({
        loadingProductivityUserIds: userIds,
      });

      return this.productivityKPIGatewayService
        .getProductivities(userIds, {
          startDatetime: startOfDay(
            new Date(visibleDateRange.start),
          ).toISOString(),
          endDatetime: endOfDay(new Date(visibleDateRange.end)).toISOString(),
        })
        .pipe(
          map((productivitiesResponse: ProductivitiesResponse) => {
            const clonedProductivitiesResponse = cloneDeep(
              ctx.getState().productivitiesResponse,
            );

            for (const [userId, userProductivity] of Object.entries(
              productivitiesResponse.usersProductivities,
            )) {
              clonedProductivitiesResponse.usersProductivities[userId] =
                userProductivity;
            }

            ctx.patchState({
              productivitiesResponse: clonedProductivitiesResponse,
              loadingProductivityUserIds: [],
            });
          }),
          catchError(() => {
            this.nzMessageService.error(
              this.translateService.instant('general.error'),
            );

            ctx.patchState({
              loadingProductivityUserIds: [],
            });
            return [];
          }),
        );
    } else {
      ctx.patchState({
        productivitiesResponse:
          defaultTimelineDataUtilityState.productivitiesResponse,
        loadingProductivityUserIds: [],
      });
      return [];
    }
  }
}
